import React, { FC } from 'react';

import classnames from 'classnames';

import { ReactComponent as AddIcon } from 'assets/images/icons/add.svg';
import { ReactComponent as EditIcon } from 'assets/images/icons/edit.svg';
import { ReactComponent as FullscreenIcon } from 'assets/images/icons/fullscreen.svg';
import { ReactComponent as TrashIcon } from 'assets/images/icons/trash.svg';
import profileImagePlaceholder from 'assets/images/profile.png';
import Button from 'components/common/ui-kit/Button';
import ModeratorDataList from 'components/moderator/ModeratorDataList';
import useAppDispatch from 'hooks/common/useAppDispatch';
import useAppSelector from 'hooks/common/useAppSelector';
import terms, { isRussianLocale } from 'i18n';
import { DataList } from 'models/moderator';
import { UserRole } from 'models/user';
import { showModal } from 'redux/actions';
import { useGetMobileCountryCodesQuery } from 'redux/api/common/enum-api';
import { selectUserRole } from 'redux/selectors';
import { selectIsDraftReadonly } from 'redux/selectors/enrollee/questionnaire';
import { formatDateYYYYMMDD } from 'utils/date';
import { useIsImpersonate } from 'utils/domain/impersonate';

import Loader from '../../../components/common/ui-kit/Loader';
import CheckListEntryValueSelectWithRoleChecker from '../../../components/moderator/CheckListEntryValueSelectWithRoleChecker';
import { useGetRecruiters } from '../../../hooks/common/api/useGetRecruiters';
import { ApplicationEducationLevel } from '../../../models/applications/enum';
import { Country } from '../../../models/common-entities';
import { COUNTRY_RUSSIA_ID, Gender } from '../../../models/enum';
import { EducationalLevel } from '../../../models/staff/upload-queue';
import {
  selectCurrentApplication,
  selectCurrentApplicationProfileImage,
  selectCurrentApplicationUser,
  selectCurrentQuestionnaire,
} from '../../../redux/selectors/staff/applications';
import '../ModeratorEducationPage/ModeratorEducationPage.scss';

const ModeratorBasicDataPage: FC = () => {
  const questionnaire = useAppSelector(selectCurrentQuestionnaire);
  const application = useAppSelector(selectCurrentApplication);
  const user = useAppSelector(selectCurrentApplicationUser);
  const role = useAppSelector(selectUserRole);

  const profileImage = useAppSelector(selectCurrentApplicationProfileImage);

  const recruiters = useGetRecruiters();
  const { data: codes } = useGetMobileCountryCodesQuery();

  const dispatch = useAppDispatch();

  const readOnly = useAppSelector(selectIsDraftReadonly);

  const isImpersonate = useIsImpersonate();

  if (!questionnaire || !application || !user || !codes) {
    return <Loader />;
  }

  const onOpenProfileImageModal = (mode: 'upload' | 'edit' | 'delete') => {
    dispatch(
      showModal({
        name: 'EDIT_PROFILE_IMAGE_MODAL',
        data: {
          mode,
          user,
          isImpersonate,
          userId: user.id,
        },
      })
    );
  };

  const scenaryDirection = application.applyingThisYear
    ? terms[ApplicationEducationLevel.PREPATORY_CURRENT_YEAR]
    : terms[ApplicationEducationLevel[application.educationLevel]];

  // TODO rewrite in future
  const primaryCitizenship: Country = (questionnaire?.generalDataForm as any)
    ?.primaryCitizenshipCountry as Country;

  const smallList: DataList = [
    {
      title: 'Гражданство:',
      label: isRussianLocale()
        ? primaryCitizenship.title
        : primaryCitizenship.titleForeign,
    },
    {
      title: 'Иностранец:',
      label: primaryCitizenship.id === COUNTRY_RUSSIA_ID ? 'Нет' : 'Да',
    },
    {
      title: 'Необходимость визы:',
      label: questionnaire.passportForm?.needVisa ? 'Да' : 'Нет',
    },
    {
      title: 'Соотечественник:',
      label: questionnaire.passportForm?.isCompatriot ? 'Да' : 'Нет',
    },
  ];

  const list: DataList = [
    {
      title: 'Уровень подготовки:',
      label: scenaryDirection || '-',
    },
    {
      title: 'Партнер:',
      label:
        recruiters.find(
          r => r.value === questionnaire.generalDataForm.recruiterId
        )?.label || '-',
    },
    {
      title: 'ID в системе:',
      label: user.idWithPrefix || '-',
    },
    {
      title: 'ID ЕПГУ ВО:',
      label:
        user?.epguIdentifiers?.find(
          item =>
            item.educationalLevel === EducationalLevel.HIGHER_EDUCATION ||
            item.educationalLevel === EducationalLevel.POSTGRADUATE
        )?.epguUniqueCodeProfile || '-',
    },
    {
      title: 'ID ЕПГУ СПО:',
      label:
        user?.epguIdentifiers?.find(
          item =>
            item.educationalLevel === EducationalLevel.PRIMARY_VOCATIONAL ||
            item.educationalLevel === EducationalLevel.SECONDARY_VOCATIONAL
        )?.epguUniqueCodeProfile || '-',
    },
    {
      title: '1C external ID',
      label: application.userExternalId?.toString() || '-',
    },
    {
      title: 'Ошибка выгрузки в 1С',
      label: application.uploadError ?? '-',
    },
    {
      title: 'Email:',
      label: user.email || '-',
    },
    {
      title: 'Фамилия:',
      label: questionnaire.generalDataForm.surname || '-',
    },
    {
      title: 'Имя:',
      label: questionnaire.generalDataForm.name || '-',
    },
    {
      title: 'Отчество:',
      label: questionnaire.generalDataForm.patronymic || '-',
    },
    {
      title: 'Дата рождения:',
      label: questionnaire.generalDataForm.birthday
        ? formatDateYYYYMMDD(questionnaire.generalDataForm.birthday)
        : '-',
    },
    {
      title: 'Пол:',
      label: questionnaire.generalDataForm.gender
        ? terms[Gender[questionnaire.generalDataForm.gender]]
        : '-',
    },
    {
      title: 'Мобильный телефон:',
      label: questionnaire.generalDataForm.mobile
        ? `${
            questionnaire.generalDataForm.mobileCountryCode
              ? codes[questionnaire.generalDataForm.mobileCountryCode]?.split(
                  ' '
                )[0]
              : ''
          }${questionnaire.generalDataForm.mobile}`
        : '-',
    },
    {
      title: 'СНИЛС:',
      label: questionnaire.generalDataForm.snils || '-',
    },
    {
      title: 'Дата СНИЛС:',
      label: questionnaire.generalDataForm.snilsIssuedAt
        ? formatDateYYYYMMDD(questionnaire.generalDataForm.snilsIssuedAt)
        : '-',
    },
  ];

  const englishName = [
    {
      title: 'Фамилия латинскими буквами:',
      label: questionnaire.generalDataForm.surnameLatin || '-',
    },
    {
      title: 'Имя латинскими буквами:',
      label: (
        <div className="mean-grade__modal">
          <span>{questionnaire.generalDataForm.nameLatin || '-'}</span>
          {(application.epguInfo?.createdFromEpgu ||
            role === UserRole.ADMIN ||
            role === UserRole.MODERATOR) && (
            <Button
              onClick={() =>
                dispatch(
                  showModal({
                    name: 'EDIT_LATIN_FIO',
                    data: {
                      educationalInformationDraftId:
                        application?.educationProfileId,
                      educationProfileId: application?.educationProfileId,
                      userId: application?.userId,
                    },
                  })
                )
              }
              theme="success"
              size="nano"
            >
              <EditIcon />
            </Button>
          )}
        </div>
      ),
    },
    {
      title: 'Отчество латинскими буквами:',
      label: questionnaire.generalDataForm.patronymicLatin || '-',
    },
  ];

  if (primaryCitizenship?.iso !== 'RU') {
    list.splice(11, 0, ...englishName);
  }

  return (
    <div className="moderator-basic-data-page">
      <div
        className={classnames(
          'moderator-basic-data-page__image',
          'profile-image-block'
        )}
      >
        <div className="profile-image-block__img-wrapper">
          <img
            src={profileImage || profileImagePlaceholder}
            alt={terms.PHOTO}
            className="profile-image-block__img"
          />
          {(role === UserRole.ADMIN || role === UserRole.MODERATOR) &&
            application?.epguInfo?.createdFromEpgu && (
              <div className="profile-image-block__controls image-controls">
                <div className="image-controls__top-row">
                  <button
                    className="image-controls__button image-controls__button--theme-rounded"
                    type="button"
                    onClick={() => onOpenProfileImageModal('delete')}
                  >
                    <TrashIcon className="image-controls__icon" />
                  </button>
                </div>
                <div className="image-controls__bottom-row">
                  <button
                    className="image-controls__button"
                    onClick={() => onOpenProfileImageModal('upload')}
                    type="button"
                  >
                    <AddIcon className="image-controls__icon" />
                    <span className="image-controls__label">
                      {terms.SELECT_PHOTO}
                    </span>
                  </button>
                  <button
                    type="button"
                    className="image-controls__button"
                    onClick={() => onOpenProfileImageModal('edit')}
                  >
                    <FullscreenIcon className="image-controls__icon" />
                    <span className="image-controls__label">
                      {terms.EDIT_PHOTO}
                    </span>
                  </button>
                </div>
              </div>
            )}
        </div>
        <ModeratorDataList list={smallList} />
      </div>
      <ModeratorDataList list={list} />
      <CheckListEntryValueSelectWithRoleChecker
        getValue={checkList => checkList.unrelatedCheckMarks['basic-data']}
        updateValue={newValue => ({
          unrelatedCheckMarks: {
            'basic-data': newValue,
          },
        })}
      />
    </div>
  );
};

export default ModeratorBasicDataPage;
